import { useTranslation } from "react-i18next";
import { Button, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { Close as CloseIcon, ChevronRight as ChevronRightIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { useAuth } from "@emberly/zenith-client";
import ResponsiveDialog from "../../../common/core/ResponsiveDialog";
import { useCallback, useMemo, useState } from "react";
import { useMission } from "../../../../providers/MissionProvider";
import NetworkStationPickerSection from "../common/NetworkStationPickerSection";
import AdjustRequestSection from "../common/AdjustRequestSection";
import MissionRequestSection from "./MissionRequestSection";
import { useStation } from "../../../../providers/StationProvider";
import { ActivityEnums, MissionRequestEnums } from "../../../../common/constants";
import axios from "axios";

const useStyles = makeStyles()(
  (theme) => ({
    titleContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    },
    closeButton: {
      marginLeft: theme.spacing(-1),
      marginRight: theme.spacing(1)
    },
    checkCircle: {
      width: "108px",
      height: "108px",
      color: theme.palette.success.main
    }
  })
);

export default function CreateMissionRequestDialog(props) {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { onClose, onOpenRequest } = props;

  const { mission, logMissionEvent } = useMission(); // TODO
  const [step, setStep] = useState(0);
  const [selectedStation, setSelectedStation] = useState(null);
  const { station, exportSettings } = useStation();
  const { getAccessTokenSilently } = useAuth();

  const [includeOwner, setIncludeOwner] = useState(true);
  const [includeRequester, setIncludeRequester] = useState(true);
  const [overrideRequester, setOverrideRequester] = useState(false);
  const [message, setMessage] = useState("");
  const [service, setService] = useState(mission?.salvageTasks?.[0]?.taskId || "");
  const [expectedDone, setExpectedDone] = useState("");
  const [referenceNumber, setReferenceNumber] = useState(-1);
  const [requestId, setRequestId] = useState("");
  const [working, setWorking] = useState(false);

  const missionRequestPreviewEntity = useMemo(() => {
    if (step !== 2) return {};

    return {
      sourceType: MissionRequestEnums.SourceType.StationToStation,
      created: new Date(),
      sender: station.displayName,
      phoneNumber: station.supportPhone,
      email: station.supportEmail,
      message,
      details: mission.details,
      target: mission.target,
      source: { name: station.displayName },
      expectedDone,
      actors: {
        owner: includeOwner ? mission.actors.owner : null,
        requester: includeRequester ? overrideRequester ? ({
          name: station.legalName,
          email: station.supportEmail,
          phoneNumber: station.supportPhone,
          location: exportSettings?.organizationLocation,
        }) : (mission.actors.ownerIsNotRequester ? mission.actors.requester : mission.actors.owner) : null,
        ownerIsNotRequester: mission.actors.ownerIsNotRequester || overrideRequester,
        contactPerson: mission.actors.contactPerson
      },
      salvageTasks: mission?.salvageTasks?.filter(t => t.taskId === service),
    };
  }, [step, selectedStation, message, service, mission, includeOwner, includeRequester, station, expectedDone, overrideRequester, exportSettings]);

  const onSelectStation = useCallback((station) => {
    if (!!station) {
      setSelectedStation(station);
      setStep(1);
    }
  }, []);

  const goBack = useCallback(() => {
    setStep(t => t - 1);
  }, []);

  const next = useCallback(() => {
    setStep(t => t + 1);
  }, [])

  const submit = useCallback(async () => {
    try {
      setWorking(true);

      const body = {
        missionId: mission.id,
        stationId: selectedStation.id,
        message,
        expectedDone,
        includeOwner,
        includeRequester,
        overrideRequester: overrideRequester ? ({
          name: station.legalName,
          email: station.supportEmail,
          phoneNumber: station.supportPhone,
          location: exportSettings?.organizationLocation,
        }) : null,
        service
      }

      const res = await axios.post("/api/missionrequest/stationtostation/request",
        body,
        {
          headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
        }
      );

      if (!!res.data) {
        logMissionEvent(ActivityEnums.Type.MissionRequestSent);
        setReferenceNumber(res.data.referenceNumber);
        setRequestId(res.data.requestId);
        setStep(3);
      }

    } catch (err) {
      console.log(err);
    } finally {
      setWorking(false);
    }

  }, [mission, selectedStation, message, expectedDone, includeOwner, includeRequester, service, getAccessTokenSilently, overrideRequester, exportSettings, station, logMissionEvent]);

  return (
    <ResponsiveDialog
      open
      onClose={working ? undefined : onClose}
      fullWidth
      maxWidth={step === 1 ? "sm" : step === 3 ? "xs" : "lg"}
    >
      {
        step < 3 ? (
          <DialogTitle className={classes.titleContainer}>

            <Stack>
              <Typography variant="subtitle2" color="textSecondary">
                {step === 0 ? t("missionRequests:createDialog:step1") : step === 1 ? t("missionRequests:createDialog:step2") : t("missionRequests:createDialog:step3")}
              </Typography>
              {step === 0 ? t("missionRequests:createDialog:titleStep1") : step === 1 ? t("missionRequests:createDialog:titleStep2") : t("missionRequests:createDialog:titleStep3")}
            </Stack>

            <IconButton onClick={onClose} size="small" color="primary" className={classes.closeButton} disabled={working}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
        ) : null
      }

      <DialogContent>
        {
          step === 0 ? (
            <NetworkStationPickerSection onSelected={onSelectStation} />
          ) : step === 1 ? (
            <AdjustRequestSection
              includeOwner={includeOwner}
              setIncludeOwner={setIncludeOwner}
              includeRequester={includeRequester}
              setIncludeRequester={setIncludeRequester}
              message={message}
              setMessage={setMessage}
              service={service}
              setService={setService}
              selectedStation={selectedStation}
              expectedDone={expectedDone}
              setExpectedDone={setExpectedDone}
              overrideRequester={overrideRequester}
              setOverrideRequester={setOverrideRequester}
            />
          ) : step === 2 ? (
            <MissionRequestSection entity={missionRequestPreviewEntity} station={selectedStation} isPreview />
          ) : step === 3 ? (
            <Stack alignItems="center" spacing={2} padding={3.5}>

              <CheckCircleIcon className={classes.checkCircle} />

              <Stack spacing={0} sx={{ width: "100%" }}>
                <Typography variant="h6" textAlign="center">{t("missionRequests:createDialog:confirmation:title")}</Typography>

                <Typography variant="body1" textAlign="center">
                  {t("missionRequests:createDialog:confirmation:descriptionPart1")}
                  &nbsp;{selectedStation?.displayName || "-"}
                  {t("missionRequests:createDialog:confirmation:descriptionPart2")}
                </Typography>

                <Typography variant="body1" textAlign="center" color="textSecondary">{t("missionRequests:createDialog:confirmation:reference")}: {referenceNumber}</Typography>
              </Stack>

              <Stack spacing={1} sx={{ width: "100%", paddingTop: 2 }}>
                <Button variant="contained" fullWidth size="large" color="primary" onClick={onClose}>
                  {t("common:close")}
                </Button>

                <Button variant="contained" fullWidth size="large" color="neutral" onClick={() => onOpenRequest(requestId)}>
                  {t("missionRequests:createDialog:confirmation:open")}
                </Button>
              </Stack>

            </Stack>
          ) : null
        }
      </DialogContent>

      {step === 1 ? (
        <DialogActions>
          <Button onClick={goBack}>{t("common:back")}</Button>
          <Button onClick={next} variant="contained" color="primary" endIcon={<ChevronRightIcon />}>{t("missionRequests:createDialog:next")}</Button>
        </DialogActions>
      ) : step === 2 ? (
        <DialogActions>
          <Button onClick={goBack} disabled={working}>{t("common:back")}</Button>
          <Button onClick={submit} disabled={working} loading={working} variant="contained" color="primary">{t("missionRequests:createDialog:send")}</Button>
        </DialogActions>
      ) : null}

    </ResponsiveDialog>
  );
}




